import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { TABLES } from '../../lib/constants';
import * as FiIcons from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';

const { FiTrendingUp, FiClock, FiCheckCircle, FiX, FiZap, FiTarget, FiBell, FiInfo, FiAlertTriangle, FiCheck, FiUserPlus } = FiIcons;

const Dashboard = () => {
  const { user, updateLocalUser } = useAuth();
  const [stats, setStats] = useState({ total: 0, pending: 0, paid: 0 });
  const [invitations, setInvitations] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);

  useEffect(() => {
    if (user) {
      loadStats();
      fetchInvites();
      fetchCharacters();
    }
  }, [user]);

  const loadStats = async () => {
    const { data: logs } = await supabase.from(TABLES.LOGS).select('value_at_log,status').eq('pilot_id', user.id);
    const total = logs?.reduce((acc, curr) => acc + (Number(curr.value_at_log) || 0), 0) || 0;
    const pending = logs?.filter(l => l.status === 'unpaid').reduce((acc, curr) => acc + (Number(curr.value_at_log) || 0), 0) || 0;
    const paid = logs?.filter(l => l.status === 'paid').reduce((acc, curr) => acc + (Number(curr.value_at_log) || 0), 0) || 0;
    setStats({ total, pending, paid });
  };

  const fetchInvites = async () => {
    const { data } = await supabase.from(TABLES.PILOT_MASTERS).select('*, master:master_id(id, username, nickname)').eq('pilot_id', user.id).eq('status', 'pending');
    setInvitations(data || []);
    setLoading(false);
  };

  const fetchCharacters = async () => {
    const { data } = await supabase.from(TABLES.CHARACTERS).select('*').eq('pilot_id', user.id);
    setCharacters(data || []);
  };

  const handleInviteAction = async (pmId, action) => {
    setProcessingId(pmId);
    try {
      if (action === 'approve') {
        await supabase.from(TABLES.PILOT_MASTERS).update({ status: 'active' }).eq('id', pmId);
      } else {
        await supabase.from(TABLES.PILOT_MASTERS).delete().eq('id', pmId);
      }
      fetchInvites();
    } catch (err) {
      alert(err.message);
    } finally {
      setProcessingId(null);
    }
  };

  const totalBaseSalary = characters.reduce((sum, char) => sum + (Number(char.base_salary) || 0), 0);
  if (loading) return <div className="animate-pulse flex items-center justify-center h-64">Loading Pilot Data...</div>;

  return (
    <div className="space-y-12 pb-20">
      <header>
        <h2 className="text-3xl md:text-4xl font-black text-gray-900 tracking-tight">Pilot Statistics</h2>
        <p className="text-gray-500 font-medium">Earnings overview and active master contracts.</p>
      </header>

      {/* Pending Invitations */}
      <AnimatePresence>
        {invitations.length > 0 && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <h3 className="text-[10px] font-black text-blue-600 uppercase tracking-widest ml-4 flex items-center gap-2">
              <FiUserPlus /> Pending Master Invitations
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {invitations.map(invite => (
                <div key={invite.id} className="bento-card border-l-4 border-l-blue-500 bg-blue-50/20 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center text-blue-600 shadow-sm border border-blue-100">
                      <FiTarget />
                    </div>
                    <div>
                      <h4 className="font-black text-gray-900">{invite.master?.nickname || invite.master?.username}</h4>
                      <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest">Wants to recruit you</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => handleInviteAction(invite.id, 'approve')} disabled={processingId === invite.id} className="p-3 bg-emerald-600 text-white rounded-xl shadow-lg hover:scale-105 active:scale-95 transition-all">
                      <FiCheck />
                    </button>
                    <button onClick={() => handleInviteAction(invite.id, 'reject')} disabled={processingId === invite.id} className="p-3 bg-red-500 text-white rounded-xl shadow-lg hover:scale-105 active:scale-95 transition-all">
                      <FiX />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-8 rounded-[2rem] border border-gray-100 shadow-sm border-l-4 border-l-blue-500">
          <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mb-4"><FiZap /></div>
          <h3 className="text-gray-400 font-bold text-[10px] uppercase tracking-widest mb-1">Monthly Pay Model</h3>
          <p className="text-3xl font-black text-gray-900">{user.is_base_pay_only ? `₱${totalBaseSalary.toLocaleString()}` : `₱${(user.monthly_cap || 0).toLocaleString()} Cap`}</p>
        </div>
        <div className="bg-white p-8 rounded-[2rem] border border-gray-100 shadow-sm">
          <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center mb-4"><FiTrendingUp /></div>
          <h3 className="text-gray-400 font-bold text-[10px] uppercase tracking-widest mb-1">Lifetime Earnings</h3>
          <p className="text-3xl font-black text-gray-900">₱{stats.total.toLocaleString()}</p>
        </div>
        <div className="bg-white p-8 rounded-[2rem] border border-gray-100 shadow-sm border-l-4 border-l-emerald-500">
          <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center mb-4"><FiCheckCircle /></div>
          <h3 className="text-gray-400 font-bold text-[10px] uppercase tracking-widest mb-1">Settled Payments</h3>
          <p className="text-3xl font-black text-emerald-600">₱{stats.paid.toLocaleString()}</p>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;